import { SxProps } from "@mui/material"
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import Tooltip from "@mui/material/Tooltip"
import React, { useRef, useState } from "react"
import MuiCheckbox from "../MuiCheckbox"
import styles from "./muiAutoComplete.module.scss"

export type Option = {
    title: string
    id: string | number
    search_key?: string
    disabled?: boolean
}

export const SELECT_ALL_ID = "__select_all__"

export const OverflowTooltip: React.FC<{ title: string; children: React.ReactElement }> = ({ title, children }) => {
    const [isOverflowing, setIsOverflowing] = useState(false)
    const ref = useRef<HTMLSpanElement>(null)

    const handleMouseEnter = () => {
        if (ref.current) {
            setIsOverflowing(ref.current.scrollWidth > ref.current.clientWidth)
        }
    }

    return (
        <Tooltip title={isOverflowing ? title : ""} placement="right" arrow>
            {React.cloneElement(children, { ref, onMouseEnter: handleMouseEnter })}
        </Tooltip>
    )
}

const EllipsisLabel: React.FC<{ title: string }> = ({ title }) => (
    <OverflowTooltip title={title}>
        <Box component="span" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" minWidth={0}>
            {title}
        </Box>
    </OverflowTooltip>
)

type RenderOptionFactoryArgs = {
    multiple: boolean
    showSelectAll: boolean
    isAllSelected: boolean
    isIndeterminate: boolean
}

export const makeRenderOption = ({ multiple, showSelectAll, isAllSelected, isIndeterminate }: RenderOptionFactoryArgs) => {
    if (multiple) {
        return (props: any, option: any, { selected }: { selected: boolean }) => (
            <Box
                component={"li"}
                {...props}
                key={`${option.id}`}
                className={`${styles.checkboxContainer} ${option.disabled ? styles.disabled : ""}`}
                sx={{ display: "flex", alignItems: "center", overflow: "hidden" }}
                onClick={(e: React.MouseEvent<HTMLLIElement>) => {
                    if (option.disabled) return
                    props?.onClick?.(e)
                }}>
                <MuiCheckbox
                    size="small"
                    disabled={option.disabled}
                    checked={option.id === SELECT_ALL_ID && showSelectAll ? isAllSelected : selected}
                    indeterminate={option.id === SELECT_ALL_ID && showSelectAll ? isIndeterminate : false}
                />
                <EllipsisLabel title={option.title} />
            </Box>
        )
    }

    return (props: any, option: any) => (
        <Box component="li" {...props} key={option.id} overflow="hidden">
            <EllipsisLabel title={option.title} />
        </Box>
    )
}

export const renderChips = (value: any, getItemProps: (opts: { index: number }) => any) => {
    if (!Array.isArray(value)) return undefined
    return value.map((option: Option, index: number) => {
        const { onDelete, ...itemProps } = getItemProps({ index })
        return (
            <Chip
                variant="autocomplete"
                label={option.title}
                key={option.id}
                {...itemProps}
                onDelete={option.disabled ? undefined : onDelete}
                onClick={() => {}}
            />
        )
    })
}

type BuildSxArgs = {
    error: boolean
    disabled: boolean
    containerHeight?: string
    size: "small" | "medium" | "large"
    sx: SxProps
    multiple?: boolean
}

export const buildAutocompleteSx = ({
    error,
    disabled,
    containerHeight,
    size,
    sx,
    multiple = false,
}: BuildSxArgs): SxProps => ({
    ...(error
        ? {
              "&.MuiAutocomplete-root:before, & .MuiAutocomplete-root:after": {
                  borderColor: "#F94054 !important", // MUI error color
              },
              "&.MuiAutocomplete-root": {
                  border: "1px solid #F94054 !important",
                  height: containerHeight ? containerHeight : "auto",
              },
          }
        : {
              "&.MuiAutocomplete-root": {
                  height: containerHeight ? containerHeight : "auto",
              },
          }),
    "& .MuiInput-root:before, & .MuiInput-root:after": {
        borderBottom: disabled ? "none !important" : undefined,
    },
    "& .MuiFormControl-root": {
        height: "100%",
    },
    ...(disabled
        ? {
              cursor: "not-allowed",
              "& .MuiInputBase-root,& .MuiInputBase-input": {
                  cursor: "not-allowed",
              },
          }
        : {}),
    ...(size === "large"
        ? {
              // A multi-select has to be free to grow as its chips wrap; a single-value control
              // keeps the fixed height it has always had.
              ...(multiple ? { minHeight: "44px !important" } : { height: "44px !important" }),
              ".MuiInputBase-root": {
                  paddingInline: "8px",
              },
              ".MuiAutocomplete-endAdornment": {
                  right: "8px",
              },
          }
        : {}),
    // MUI only collapses the chips to "+N" while the control is unfocused, so opening a
    // multi-select that has a large selection renders every chip at once. Left unbounded they
    // wrap past the control and sit on top of the filter below it, so cap the chip area and
    // let it scroll instead.
    ...(multiple
        ? {
              "& .MuiInputBase-root": {
                  maxHeight: "88px",
                  overflowY: "auto",
                  alignContent: "flex-start",
              },
          }
        : {}),
    ...sx,
})
